import { FC, useCallback, useMemo } from "react";
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  PopoverOrigin,
} from "@mui/material";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import {
  CreateWorkflowInLiveResourcesArgs,
  useCreateWorkflow,
} from "../../../../../../../../../utils/hooks/useCreateWorkflow.hook";
import {
  LiveResourcesCreateWorkflow,
  LiveResourcesCreateWorkflowCustomTrigger,
} from "../../../../../../../../common/LiveResourcesCreateWorkflow";
import { generateWorkflowsResourceFilter } from "../../../../../../../../resources-area/toolbar/actions/utils/helpers/generateWorkflowsResourceFilter";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setWorkflowCreationDrawerOpen } from "@/store/schedules/schedulesSlice";
import {
  AccountLight,
  ResourceType,
  ScheduleActionType,
} from "@/services/cloudchipr.api";
import { getFirstParentInHierarchyResourceType } from "@/utils/helpers/resources/getFirstParentInHierarchyResourceType";
import { liveUsageMgmtAccountsWorkflowDataSelector } from "@/store/live-usage-mgmt/selectors/accounts-data/liveUsageMgmtAccountsWorkflowDataSelector";

interface CreateWorkflowMenuItemProps {
  uniqueIdentifier: string;
  resourceType: ResourceType;
  onClose(): void;
  account: AccountLight;
}

export const CreateWorkflowMenuItem: FC<CreateWorkflowMenuItemProps> = ({
  onClose,
  resourceType,
  account,
  uniqueIdentifier,
}) => {
  const dispatch = useAppDispatch();

  const accountsWorkflowData = useAppSelector((state) =>
    liveUsageMgmtAccountsWorkflowDataSelector(state, resourceType),
  );

  const data = useMemo(() => {
    if (!accountsWorkflowData) {
      return null;
    }

    const parentResourceType =
      getFirstParentInHierarchyResourceType(resourceType) ?? resourceType;
    const filters = generateWorkflowsResourceFilter(
      parentResourceType,
      resourceType,
      [uniqueIdentifier],
    );

    return {
      ...accountsWorkflowData,
      filters,
      resourceType,
      accounts: [account],
    } as CreateWorkflowInLiveResourcesArgs;
  }, [resourceType, uniqueIdentifier, accountsWorkflowData, account]);

  const createWorkflow = useCreateWorkflow(data);

  const createHandler = useCallback(
    (action: ScheduleActionType) => {
      createWorkflow(action);
      dispatch(setWorkflowCreationDrawerOpen(true));
      onClose();
    },
    [dispatch, onClose, createWorkflow],
  );

  return (
    <LiveResourcesCreateWorkflow
      onItemClick={createHandler}
      anchorOrigin={anchorOrigin}
      CustomTrigger={CustomTrigger}
      transformOrigin={transformOrigin}
      disableActionItems={account.provider_access_type === "read"}
    />
  );
};

const CustomTrigger: LiveResourcesCreateWorkflowCustomTrigger = ({
  onClick,
  disabled,
}) => {
  return (
    <MenuItem dense onClick={onClick} disabled={disabled}>
      <ListItemIcon>
        <AvTimerIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Schedule a Workflow</ListItemText>
    </MenuItem>
  );
};

const anchorOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "left",
};

const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};
